import { useEffect, useState } from "react";
import { Loader2, PlugZap, RefreshCw, ShieldCheck, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/pages/Leads";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function EwaySettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [denied, setDenied] = useState(false);
  const [favs, setFavs] = useState({ vehicles: [], transporters: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/eway/config");
      setData(d);
      setDenied(!d.is_owner);
      const { data: f } = await api.get("/eway/favourites");
      setFavs(f);
    } catch (e) {
      setDenied(true);
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (provider) => {
    setBusy("save");
    try {
      const { data: d } = await api.put("/eway/config", { provider });
      setData(d);
      toast.success("E-way bill provider updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data: r } = await api.post("/eway/test-connection");
      toast.success(r.mock ? "Simulator connected — no government call was made"
        : "Connected to the e-way bill service");
      load();
    } catch (e) {
      toast.error(apiError(e));
      load();
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="eway-settings">
      <PageHeader
        testId="eway-settings-header"
        title="GST / E-Way Bill Integration"
        subtitle="Owner only. Credentials live on the server and are never shown here."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="gap-1.5" disabled={busy === "test"}
              onClick={test} data-testid="eway-test-connection">
              {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" />
                : <PlugZap className="h-4 w-4" />} Test connection
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={load}
              data-testid="eway-settings-refresh">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        }
      />

      {loading && (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin" /></div>
      )}

      {!loading && denied && (
        <div className="surface p-4 text-sm" data-testid="eway-settings-denied">
          Only the Owner can view or change the e-way bill integration.
        </div>
      )}

      {!loading && data?.is_owner && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="surface space-y-3 p-4" data-testid="eway-provider-card">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg">Connection</h2>
              <Badge variant={data.configured ? "default" : "secondary"}
                data-testid="eway-connection-status">
                {data.configured ? "Connected" : "Disconnected"}
              </Badge>
            </div>
            <Field label="Provider">
              <Select value={data.provider || ""} onValueChange={save} disabled={busy === "save"}>
                <SelectTrigger data-testid="eway-provider-select">
                  <SelectValue placeholder="Not configured" />
                </SelectTrigger>
                <SelectContent>
                  {(data.choices || []).map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {data.mock && (
              <p className="text-xs text-amber-600" data-testid="eway-mock-warning">
                The simulator never contacts the government. Numbers it returns are clearly marked
                MOCK and must never be used on a real consignment.
              </p>
            )}
            <div className="grid gap-1 text-xs">
              {[["Mode", data.mode],
                ["GSTIN", data.gstin || "—"],
                ["Last successful authentication", data.last_authenticated_at || "never"],
                ["Last successful generation", data.last_generated_at || "never"],
                ["Last error", data.last_error || "none"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b py-1 last:border-0">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="text-right font-medium break-all">{String(v)}</span>
                  </div>
              ))}
            </div>
          </div>

          <div className="surface space-y-3 p-4" data-testid="eway-credentials-card">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              <h2 className="text-base md:text-lg">Credentials on the server</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              Values are never returned to the browser. Add them to the backend environment, then
              restart the backend and press Test connection.
            </p>
            {Object.keys(data.credentials_present || {}).length === 0 && (
              <p className="text-xs" data-testid="eway-no-credentials-needed">
                This provider needs no credentials.
              </p>
            )}
            <div className="grid gap-1 text-xs">
              {Object.entries(data.credentials_present || {}).map(([k, ok]) => (
                <div key={k} className="flex justify-between gap-3 border-b py-1 last:border-0"
                  data-testid={`eway-cred-${k}`}>
                  <span className="font-mono-num text-[11px]">{k}</span>
                  <Badge variant={ok ? "default" : "secondary"} className="text-[10px]">
                    {ok ? "set" : "missing"}
                  </Badge>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Sandbox first: validate authentication, generation, invalid GSTIN/HSN, vehicle
              validation, timeouts and duplicate clicks before switching EWAY_MODE to production.
            </p>
          </div>

          <div className="surface space-y-3 p-4 lg:col-span-2" data-testid="eway-shortcuts-card">
            <h2 className="text-base md:text-lg">Vehicle &amp; transporter shortcuts</h2>
            <p className="text-xs text-muted-foreground">
              Built automatically from the bills you generate — most used first. Remove a wrong
              entry and it stops appearing; nothing on past bills changes.
            </p>
            {["vehicles", "transporters"].map((group) => (
              <div key={group}>
                <div className="text-xs font-medium capitalize">{group}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(favs[group] || []).length === 0 && (
                    <span className="text-xs text-muted-foreground">Nothing saved yet</span>
                  )}
                  {(favs[group] || []).map((s) => (
                    <span key={s.value}
                      data-testid={`eway-fav-${group}-${s.value}`}
                      className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]">
                      {s.value}
                      <span className="text-muted-foreground">×{s.used_count}</span>
                      <button type="button" className="text-destructive"
                        data-testid={`eway-fav-remove-${s.value}`}
                        onClick={async () => {
                          try {
                            const { data: f } = await api.delete(
                              `/eway/favourites/${group === "vehicles" ? "vehicle" : "transporter"}`
                              + `/${encodeURIComponent(s.value)}`);
                            setFavs(f);
                            toast.success("Shortcut removed");
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
